import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Home,
  ChevronRight,
  Loader,
  Download,
  CheckCircle,
  Layers,
  Zap,
  Shield,
  Plus,
  X,
  Files,
  FileType,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import SharePointBrowser from "../components/SharePointBrowser";
import Swal from "sweetalert2";
import { API_BASE_URL } from "../config/api.config";

interface SelectedFile {
  driveId: string;
  fileId: string;
  fileName: string;
  driveName: string;
}

// crypto.randomUUID() only exists in secure contexts (HTTPS or localhost) -
// fall back to a plain random id so this still works over plain HTTP.
function generateJobId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `job_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export default function PptxConverter() {
  const navigate = useNavigate();

  // SharePoint browser state
  const [sharePointBrowserOpen, setSharePointBrowserOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);

  // Form state
  const [slidesPerPage, setSlidesPerPage] = useState<1 | 2 | 4 | "all">(4); // PPTXC-RQ-08: 1 slide option; 'all' = generate 1, 2 and 4 versions in one go
  const [converting, setConverting] = useState(false);
  const [outputFileName, setOutputFileName] = useState("");

  // Custom combination mode: pick exactly which layouts to generate (e.g. 1 + 3, or 2 + 3)
  // instead of being limited to the fixed 1/2/4/All Formats presets above.
  const [customMode, setCustomMode] = useState(false);
  const [customLayouts, setCustomLayouts] = useState<number[]>([]);

  const toggleCustomLayout = (layout: number) => {
    setCustomLayouts((prev) =>
      prev.includes(layout)
        ? prev.filter((l) => l !== layout)
        : [...prev, layout].sort(),
    );
  };

  // NPPTXC-RQ-5: output format for the dedicated 1-slide-per-page layout —
  // PDF (default), editable merged PPTX, or both. Other layouts stay PDF-only.
  const [outputFormat, setOutputFormat] = useState<"pdf" | "pptx" | "both">("pdf");

  // The value actually sent to the backend: the fixed preset, or the custom array when in custom mode
  const effectiveSlidesPerPage: 1 | 2 | 4 | "all" | number[] = customMode
    ? customLayouts
    : slidesPerPage;
  // outputFormat is only honored on the dedicated 1-slide selection (NPPTXC-RQ-5)
  const effectiveOutputFormat =
    !customMode && slidesPerPage === 1 ? outputFormat : "pdf";
  const isMultiOutput =
    customMode || slidesPerPage === "all" || effectiveOutputFormat !== "pdf";

  // Progress tracking: conversion time broken down section-by-section (e.g.
  // "Convert PPTX to PDF", "Arrange 2 slides/page"), streamed over SSE so the
  // user sees real step-by-step status instead of one opaque spinner.
  interface ProgressSection {
    name: string;
    status: "pending" | "in_progress" | "completed" | "error";
    elapsedMs?: number;
  }
  const [progressSections, setProgressSections] = useState<ProgressSection[]>(
    [],
  );

  const trackConversionProgress = (jobId: string): (() => void) => {
    setProgressSections([]);
    const es = new EventSource(
      `${API_BASE_URL}/pptx-converter/progress/${jobId}`,
    );

    es.addEventListener("progress", (event: MessageEvent) => {
      const data = JSON.parse(event.data);
      if (data.done) {
        es.close();
        return;
      }
      setProgressSections((prev) => {
        const next = [...prev];
        while (next.length <= data.sectionIndex) {
          next.push({ name: "", status: "pending" });
        }
        next[data.sectionIndex] = {
          name: data.section,
          status:
            data.status === "started"
              ? "in_progress"
              : data.status === "completed"
                ? "completed"
                : "error",
          elapsedMs: data.elapsedMs,
        };
        return next;
      });
    });

    es.onerror = () => es.close();

    return () => es.close();
  };

  // Handle SharePoint file selection
  const handleSharePointSelect = (
    driveId: string,
    driveName: string,
    folderPath?: string,
    _folderId?: string,
    fileName?: string,
    fileId?: string,
    selectedFilesFromModal?: any[],
  ) => {
    // Handle multi-file selection
    if (selectedFilesFromModal && selectedFilesFromModal.length > 0) {
      const newFiles = selectedFilesFromModal
        .filter((file) => !selectedFiles.some((f) => f.fileId === file.id))
        .map((file) => ({
          driveId,
          fileId: file.id,
          fileName: file.name,
          driveName: folderPath ? `${driveName}/${folderPath}` : driveName,
        }));

      if (newFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...newFiles]);
        Swal.fire(
          "Success",
          `Added ${newFiles.length} file(s) to the list`,
          "success",
        );
      } else {
        Swal.fire("Info", "All selected files are already in the list", "info");
      }
    }
    // Handle single file selection (backward compatibility)
    else if (fileName && fileId) {
      const alreadyAdded = selectedFiles.some((f) => f.fileId === fileId);
      if (alreadyAdded) {
        Swal.fire("Info", "This file is already added to the list", "info");
        return;
      }

      setSelectedFiles((prev) => [
        ...prev,
        {
          driveId,
          fileId,
          fileName,
          driveName: folderPath ? `${driveName}/${folderPath}` : driveName,
        },
      ]);
    }
    setSharePointBrowserOpen(false);
  };

  // Remove file from selection
  const handleRemoveFile = (fileId: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.fileId !== fileId));
  };

  // PPTXC-RQ-11: Move file up in the list
  const handleMoveFileUp = (index: number) => {
    if (index === 0) return; // Already at top
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      [newFiles[index - 1], newFiles[index]] = [
        newFiles[index],
        newFiles[index - 1],
      ];
      return newFiles;
    });
  };

  // PPTXC-RQ-11: Move file down in the list
  const handleMoveFileDown = (index: number) => {
    if (index === selectedFiles.length - 1) return; // Already at bottom
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      [newFiles[index], newFiles[index + 1]] = [
        newFiles[index + 1],
        newFiles[index],
      ];
      return newFiles;
    });
  };

  // Convert single or multiple PPTXs to PDF
  const handleBatchConvert = async () => {
    if (selectedFiles.length === 0) {
      Swal.fire(
        "Error",
        "Please select at least one PPTX file from SharePoint",
        "error",
      );
      return;
    }

    if (customMode && customLayouts.length === 0) {
      Swal.fire(
        "Error",
        "Select at least one layout for the custom combination",
        "error",
      );
      return;
    }

    const jobId = generateJobId();
    const stopTracking = trackConversionProgress(jobId);

    try {
      setConverting(true);

      // SINGLE FILE: Use /convert endpoint (direct conversion, no merge)
      if (selectedFiles.length === 1) {
        const file = selectedFiles[0];
        const requestBody = {
          fileId: file.fileId,
          driveId: file.driveId,
          fileName: file.fileName,
          slidesPerPage: effectiveSlidesPerPage,
          outputFormat: effectiveOutputFormat,
          jobId,
        };

        const response = await fetch(`${API_BASE_URL}/pptx-converter/convert`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: "Failed to convert PPTX to PDF" }));
          throw new Error(errorData.error || "Failed to convert PPTX to PDF");
        }

        if (isMultiOutput) {
          // 'All formats' / custom combination returns JSON with the generated versions - download each one
          const result = await response.json();
          const conversions = result?.data?.conversions || [];

          for (const conversion of conversions) {
            const downloadResponse = await fetch(
              `${API_BASE_URL}/pptx-converter/download/${conversion.id}`,
            );
            if (!downloadResponse.ok) continue;

            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download =
              conversion.outputFileName ||
              file.fileName.replace(
                /\.pptx$/i,
                `_${conversion.slidesPerPage}_slides.pdf`,
              );
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }

          await Swal.fire({
            icon: "success",
            title: "Conversion Complete!",
            html: `
              <p><strong>${file.fileName}</strong> converted to ${conversions.length} file${conversions.length > 1 ? "s" : ""}!</p>
              <p class="text-sm text-gray-600 mt-2">${
                effectiveOutputFormat !== "pdf"
                  ? `1 slide-per-page ${effectiveOutputFormat === "both" ? "PDF + editable PPTX" : "editable PPTX"}`
                  : `${customMode ? customLayouts.join(", ") : "1, 2 and 4"} slides-per-page version(s)`
              } downloaded and saved to S3.</p>
            `,
            confirmButtonText: "View History",
            showCancelButton: true,
            cancelButtonText: "Stay Here",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate("/pptx-history");
            }
          });
        } else {
          // Single file returns PDF blob directly
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.fileName.replace(
            /\.pptx$/i,
            `_${slidesPerPage}_slides.pdf`,
          );
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);

          await Swal.fire({
            icon: "success",
            title: "Conversion Complete!",
            html: `
              <p><strong>${file.fileName}</strong> converted to PDF!</p>
              <p class="text-sm text-gray-600 mt-2">File downloaded and saved to S3.</p>
            `,
            confirmButtonText: "View History",
            showCancelButton: true,
            cancelButtonText: "Stay Here",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate("/pptx-history");
            }
          });
        }
      }
      // MULTIPLE FILES: Use /convert-batch endpoint (merge + convert)
      else {
        const filesArray = selectedFiles.map((file) => ({
          fileId: file.fileId,
          driveId: file.driveId,
          fileName: file.fileName,
        }));

        const requestBody: any = {
          files: filesArray,
          slidesPerPage: effectiveSlidesPerPage,
          outputFormat: effectiveOutputFormat,
          jobId,
        };

        // Add output filename if provided (backend strips the extension)
        if (outputFileName.trim()) {
          requestBody.outputFileName = /\.(pdf|pptx)$/i.test(
            outputFileName.trim(),
          )
            ? outputFileName.trim()
            : `${outputFileName.trim()}.pdf`;
        }

        const response = await fetch(
          `${API_BASE_URL}/pptx-converter/convert-batch`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
          },
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: "Failed to convert PPTX files to PDF" }));
          throw new Error(
            errorData.error || "Failed to convert PPTX files to PDF",
          );
        }

        const batchResult = await response.json();
        const batchConversionsCount = batchResult?.data?.conversions?.length;

        await Swal.fire({
          icon: "success",
          title: "Batch Conversion Complete!",
          html: isMultiOutput
            ? `
            <p><strong>${selectedFiles.length} files merged</strong> into ${batchConversionsCount || ""} file${(batchConversionsCount || 0) > 1 ? "s" : ""}!</p>
            <p class="text-sm text-gray-600 mt-2">${
              effectiveOutputFormat !== "pdf"
                ? `1 slide-per-page ${effectiveOutputFormat === "both" ? "PDF + editable merged PPTX" : "editable merged PPTX"}`
                : `${customMode ? customLayouts.join(", ") : "1, 2 and 4"} slides-per-page version(s)`
            } saved to S3 and available in history.</p>
          `
            : `
            <p><strong>${selectedFiles.length} files merged</strong> into one PDF!</p>
            <p class="text-sm text-gray-600 mt-2">File saved to S3 and available in history.</p>
          `,
          confirmButtonText: "View History",
          showCancelButton: true,
          cancelButtonText: "Stay Here",
        }).then((result) => {
          if (result.isConfirmed) {
            navigate("/pptx-history");
          }
        });
      }

      // Reset form
      setSelectedFiles([]);
      setOutputFileName("");
    } catch (error: any) {
      console.error("Error converting PPTX files:", error);
      Swal.fire(
        "Error",
        error.message || "Failed to convert PPTX files to PDF",
        "error",
      );
    } finally {
      setConverting(false);
      stopTracking();
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-2 text-sm text-gray-600">
        <Link
          to="/dashboard"
          className="flex items-center hover:text-blue-600 transition-colors"
        >
          <Home className="w-4 h-4" />
        </Link>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-400">Slides</span>
        <ChevronRight className="w-4 h-4" />
        <span className="text-gray-900 font-semibold">PPTX Converter</span>
      </nav>

      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Files className="w-8 h-8" />
          PPTX Batch Converter
        </h1>
        <p className="mt-2 text-gray-600">
          Merge multiple PowerPoint presentations into ONE PDF
        </p>
      </div>

      {/* Features Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-600 font-semibold">
                Multiple Files
              </p>
              <p className="text-2xl font-bold text-blue-900 mt-1">Merge</p>
            </div>
            <Files className="w-12 h-12 text-blue-600 opacity-50" />
          </div>
        </div>
        <div className="bg-orange-50 rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-600 font-semibold">
                1, 2 or 4 Slides
              </p>
              <p className="text-2xl font-bold text-orange-900 mt-1">
                Per Page
              </p>
            </div>
            <Layers className="w-12 h-12 text-orange-600 opacity-50" />
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-600 font-semibold">Fast</p>
              <p className="text-2xl font-bold text-green-900 mt-1">
                Processing
              </p>
            </div>
            <Zap className="w-12 h-12 text-green-600 opacity-50" />
          </div>
        </div>
        <div className="bg-purple-50 rounded-xl p-6 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-600 font-semibold">
                SharePoint
              </p>
              <p className="text-2xl font-bold text-purple-900 mt-1">
                Integration
              </p>
            </div>
            <Shield className="w-12 h-12 text-purple-600 opacity-50" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {selectedFiles.length === 0 ? (
          /* Initial State - No Files */
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-md ring-4 ring-blue-50">
              <Files className="w-12 h-12 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Select Multiple PPTX Files
            </h2>
            <p className="text-gray-600 mb-8">
              Browse SharePoint and select PowerPoint presentations to merge
              into a single PDF — or an editable PPTX (one slide per page)
            </p>

            <button
              onClick={() => setSharePointBrowserOpen(true)}
              className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all duration-200 shadow-lg hover:shadow-2xl transform hover:-translate-y-0.5"
            >
              <Plus className="w-6 h-6" />
              Add Files from SharePoint
            </button>

            {/* Instructions */}
            <div className="mt-12 p-8 bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl text-left w-full border border-blue-200 shadow-lg">
              <h3 className="font-bold text-gray-900 mb-5 flex items-center gap-2 text-base">
                <span className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Files className="w-5 h-5 text-white" />
                </span>
                How Batch Conversion Works
              </h3>
              <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    1
                  </span>
                  <span className="pt-1">
                    <strong>Add Multiple Files:</strong> Click "Add Files" and
                    select your first PPTX file
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    2
                  </span>
                  <span className="pt-1">
                    <strong>Keep Adding:</strong> Repeat to add more PPTX files
                    (optional - single file also supported)
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    3
                  </span>
                  <span className="pt-1">
                    <strong>Order Matters:</strong> Files will be merged in the
                    order you add them
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    4
                  </span>
                  <span className="pt-1">
                    <strong>Choose Layout:</strong> 1 (landscape), 2 or 4 slides
                    per page, All Formats (1+2+4), or a Custom combination —
                    exactly the versions you need
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    5
                  </span>
                  <span className="pt-1">
                    <strong>Pick Output Format:</strong> for the 1-slide layout,
                    choose PDF, an editable merged PPTX, or both
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-sm">
                    6
                  </span>
                  <span className="pt-1">
                    <strong>Merge & Convert:</strong> one click creates your
                    combined file(s), saved to history automatically
                  </span>
                </li>
              </ol>
            </div>
          </div>
        ) : (
          /* Files Selected State */
          <div className="w-full space-y-6">
            {/* Selected Files List */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-xl p-6 shadow-md">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </span>
                  {selectedFiles.length} File
                  {selectedFiles.length > 1 ? "s" : ""} Selected
                </h3>
                <button
                  onClick={() => setSharePointBrowserOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold text-sm hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  <Plus className="w-4 h-4" />
                  Add More Files
                </button>
              </div>

              <div className="space-y-3">
                {selectedFiles.map((file, index) => (
                  <div
                    key={file.fileId}
                    className="group flex items-center gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-lg hover:border-blue-300 transition-all duration-200"
                  >
                    <div className="flex-shrink-0 w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-lg flex items-center justify-center font-bold text-sm shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-shrink-0 w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center">
                      <FileType className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {file.fileName}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {file.driveName}
                      </p>
                    </div>

                    {/* PPTXC-RQ-11: Reorder buttons */}
                    <div className="flex-shrink-0 flex flex-col rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                      <button
                        onClick={() => handleMoveFileUp(index)}
                        disabled={index === 0}
                        className="px-2 py-1 text-gray-500 bg-gray-50 hover:bg-blue-100 hover:text-blue-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-500 border-b border-gray-200"
                        title="Move up"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleMoveFileDown(index)}
                        disabled={index === selectedFiles.length - 1}
                        className="px-2 py-1 text-gray-500 bg-gray-50 hover:bg-blue-100 hover:text-blue-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-500"
                        title="Move down"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemoveFile(file.fileId)}
                      className="flex-shrink-0 p-2 text-red-500 bg-red-50 hover:bg-red-100 hover:text-red-700 rounded-lg transition-colors shadow-sm"
                      title="Remove file"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Output Filename */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <label className="block text-sm font-bold text-gray-900 mb-3">
                Output PDF Name (Optional)
              </label>
              <input
                type="text"
                value={outputFileName}
                onChange={(e) => setOutputFileName(e.target.value)}
                placeholder="e.g., Combined_Presentation"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:shadow-md transition-shadow outline-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                Leave empty for auto-generated name. Extension (.pdf) will be
                added automatically.
              </p>
            </div>

            {/* Slides Per Page Selector */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
              <label className="block text-sm font-bold text-gray-900 mb-4">
                Slides Per Page
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <button
                  onClick={() => {
                    setCustomMode(false);
                    setSlidesPerPage(1);
                  }}
                  className={`p-6 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${
                    !customMode && slidesPerPage === 1
                      ? "bg-blue-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400 shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Layers
                      className={`w-8 h-8 ${!customMode && slidesPerPage === 1 ? "text-white" : "text-gray-400"}`}
                    />
                    <span>1 Slide</span>
                    <span className="text-xs opacity-75">Landscape</span>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setCustomMode(false);
                    setSlidesPerPage(2);
                  }}
                  className={`p-6 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${
                    !customMode && slidesPerPage === 2
                      ? "bg-blue-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400 shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Layers
                      className={`w-8 h-8 ${!customMode && slidesPerPage === 2 ? "text-white" : "text-gray-400"}`}
                    />
                    <span>2 Slides</span>
                    <span className="text-xs opacity-75">Booklet</span>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setCustomMode(false);
                    setSlidesPerPage(4);
                  }}
                  className={`p-6 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${
                    !customMode && slidesPerPage === 4
                      ? "bg-blue-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400 shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Layers
                      className={`w-8 h-8 ${!customMode && slidesPerPage === 4 ? "text-white" : "text-gray-400"}`}
                    />
                    <span>4 Slides</span>
                    <span className="text-xs opacity-75">Compact</span>
                  </div>
                </button>

                <button
                  onClick={() => {
                    setCustomMode(false);
                    setSlidesPerPage("all");
                  }}
                  className={`p-6 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${
                    !customMode && slidesPerPage === "all"
                      ? "bg-purple-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-purple-400 shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Files
                      className={`w-8 h-8 ${!customMode && slidesPerPage === "all" ? "text-white" : "text-gray-400"}`}
                    />
                    <span>All Formats</span>
                    <span className="text-xs opacity-75">1 + 2 + 4</span>
                  </div>
                </button>

                <button
                  onClick={() => setCustomMode(true)}
                  className={`p-6 rounded-xl font-bold text-lg transition-all duration-200 transform hover:-translate-y-1 hover:shadow-xl ${
                    customMode
                      ? "bg-teal-600 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-300 hover:border-teal-400 shadow-md"
                  }`}
                >
                  <div className="flex flex-col items-center gap-3">
                    <Files
                      className={`w-8 h-8 ${customMode ? "text-white" : "text-gray-400"}`}
                    />
                    <span>Custom</span>
                    <span className="text-xs opacity-75">Pick any combo</span>
                  </div>
                </button>
              </div>

              {customMode && (
                <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-xl shadow-inner">
                  <p className="text-sm font-semibold text-gray-900 mb-3">
                    Select exactly which layouts to generate:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {[1, 2, 3, 4].map((layout) => (
                      <label
                        key={layout}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all shadow-sm hover:shadow-md ${
                          customLayouts.includes(layout)
                            ? "bg-teal-600 text-white border-teal-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-teal-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={customLayouts.includes(layout)}
                          onChange={() => toggleCustomLayout(layout)}
                        />
                        <span className="font-bold">
                          {layout} Slide{layout > 1 ? "s" : ""}/Page
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 text-sm text-teal-700">
                    The file is converted once and only the layout(s) you tick
                    above are generated — no extra versions forced in.
                  </p>
                </div>
              )}

              {!customMode && slidesPerPage === "all" && (
                <p className="mt-4 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-3">
                  The files are converted once and all three versions (1, 2 and
                  4 slides per page) are generated together — no need to select
                  the files again.
                </p>
              )}

              {/* NPPTXC-RQ-5: output format for the 1-slide layout (PDF / editable PPTX / both) */}
              {!customMode && slidesPerPage === 1 && (
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl shadow-inner">
                  <p className="text-sm font-semibold text-gray-900 mb-3">
                    Output format:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {(
                      [
                        { value: "pdf", label: "PDF" },
                        { value: "pptx", label: "PPTX (editable)" },
                        { value: "both", label: "Both" },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setOutputFormat(opt.value)}
                        className={`px-4 py-2 rounded-lg border-2 font-bold transition-all shadow-sm hover:shadow-md ${
                          outputFormat === opt.value
                            ? "bg-blue-600 text-white border-blue-600"
                            : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  {outputFormat !== "pdf" && (
                    <p className="mt-3 text-sm text-blue-700">
                      {selectedFiles.length > 1
                        ? "All selected files are merged into ONE editable PowerPoint file (one slide per page), preserving the content of each presentation."
                        : "The selected file is delivered as an editable PowerPoint file (one slide per page)."}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Progress Breakdown: time per section, streamed live during conversion */}
            {converting && progressSections.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2 shadow-md">
                {progressSections.map((section, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      {section.status === "completed" && (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                      {section.status === "in_progress" && (
                        <Loader className="w-4 h-4 text-blue-600 animate-spin" />
                      )}
                      {section.status === "error" && (
                        <X className="w-4 h-4 text-red-600" />
                      )}
                      {section.status === "pending" && (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                      <span
                        className={
                          section.status === "pending"
                            ? "text-gray-400"
                            : "text-gray-800 font-medium"
                        }
                      >
                        {section.name}
                      </span>
                    </div>
                    {section.status === "completed" &&
                      section.elapsedMs !== undefined && (
                        <span className="text-xs text-gray-500">
                          {(section.elapsedMs / 1000).toFixed(1)}s
                        </span>
                      )}
                  </div>
                ))}
              </div>
            )}

            {/* Convert Button */}
            <button
              onClick={handleBatchConvert}
              disabled={
                converting ||
                selectedFiles.length === 0 ||
                (customMode && customLayouts.length === 0)
              }
              className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-2xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-lg"
            >
              {converting ? (
                <>
                  <Loader className="w-6 h-6 animate-spin" />
                  {selectedFiles.length === 1
                    ? "Converting..."
                    : `Merging & Converting ${selectedFiles.length} Files...`}
                </>
              ) : (
                <>
                  <Download className="w-6 h-6" />
                  {selectedFiles.length === 1
                    ? "Convert to PDF"
                    : "Merge & Convert to PDF"}
                </>
              )}
            </button>

            {selectedFiles.length === 0 && (
              <p className="text-center text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Add at least 1 file to enable conversion
              </p>
            )}
          </div>
        )}
      </div>

      {/* SharePoint Browser Dialog */}
      <SharePointBrowser
        open={sharePointBrowserOpen}
        onClose={() => setSharePointBrowserOpen(false)}
        onSelect={handleSharePointSelect}
        title="Select PPTX Files"
        selectionMode="multi-file"
        fileFilter={[".pptx", ".ppt"]}
        rememberLocation={true}
        initialPath={{
          siteName: "FK Partners Portal",
          driveName: "Conteúdo",
          folderPath:
            "/1. Objetos de Aprendizagem/000 - apenas SLIDES - Objetos apostilas",
        }}
      />
    </div>
  );
}
